import { Button, IconButton, Tag, Tooltip } from '@carbon/react';
import { CheckmarkFilled, Information, Undo } from '@carbon/react/icons';
import { createColumnHelper, type Row } from '@tanstack/react-table';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  DataTable,
  type DataTableColumnDef,
  type InitialTableState,
  type TableState,
} from 'components/data-table';
import { formatUnitAgreementType } from 'components/allocation-manager/utils';
import { Link } from 'components/link';
import { ServerExportStatusBanner } from 'components/server-export-status-banner';
import {
  TotalUnitInterestCard,
  type UnitInterestTableItem,
} from 'components/total-unit-interest-card';
import { format0, format8 } from 'utils/number';

import { SlotFilters } from './slot-filters';
import { SubtractsTable } from './subtracts-table';
import { SummaryTiles } from './summary-tiles';
import {
  comparisonTableSelectionAtom,
  confirmPanelStateAtom,
  openConfirmPanelAtom,
  openSlotDetailAtom,
  openUnconfirmModalAtom,
  slotListAtomFamily,
  workspaceFilterValuesAtomFamily,
} from '../atoms';
import { useSlotsExport } from '../hooks';
import type { SlotInterestRow } from '../types';
import {
  columnFiltersToParams,
  DEFAULT_PARAMS,
  evidenceSourceLabel,
  interestFormula,
  interestSourceLabel,
  isAmSourceSelectable,
  resolveSortAttribute,
  rowKey,
  SLOT_STATUS_LABELS,
  slotStatus,
  STATUS_DISPLAY,
  subtractsFraction,
  streamsLabel,
  WELL_STATUS_LABELS,
} from '../utils';
import { useWorkspaceConfirmation } from '../workspace-confirmation-context';
import styles from './comparison-table.module.scss';

// The DataTable wrapper's generic is `<TData extends Record<string, unknown>>`.
// `SlotInterestRow` lacks the implicit index signature, so the call site casts
// to `SlotRow` (this intersection). Mirrors AFE Catalog's `AfeRow` pattern.
// `numberOfLines` is the wrapper's per-row row-height knob (cf.
// `components/data-table/table-body.tsx` `getRowLines`) — declared here so the
// stamp at `tableData` is type-checked rather than swallowed by the index sig.
type SlotRow = SlotInterestRow & {
  numberOfLines?: number;
} & Record<string, unknown>;

const columnHelper = createColumnHelper<SlotInterestRow>();

/**
 * Builds the per-subtract item array consumed by `TotalUnitInterestCard`. Each
 * entry contributes its AM or GIS NRI / WI to the slot's rollup. Formula inputs
 * (`value`, `valueUnit`, `factor`) are not yet emitted by the list endpoint —
 * the breakdown table renders the available columns and skips the rest.
 *
 * @param {SlotInterestRow} slot - The slot row.
 * @param {'am' | 'gis'} source - Which column pair to roll up.
 * @param {'NRI' | 'WI'} calculationType - Which value of the pair.
 * @return {UnitInterestTableItem[]} The breakdown rows.
 */
const buildSlotInterestItems = (
  slot: SlotInterestRow,
  source: 'am' | 'gis',
  calculationType: 'NRI' | 'WI'
): UnitInterestTableItem[] =>
  slot.subtracts.map((s) => {
    const pair =
      source === 'am'
        ? { NRI: s.amNri, WI: s.amWi }
        : { NRI: s.gisNri, WI: s.gisWi };
    return {
      subtract: s.subtractName,
      subtractSalesforceId: s.subtractId,
      tractSalesforceId: s.tractId ?? undefined,
      uat: s.uatName ?? '',
      uatInterest: pair[calculationType] ?? 0,
      nriCalculationId: s.nriCalculationId ?? undefined,
    };
  });

/**
 * Renders a slot-level AM / GIS NRI / WI cell — the formatted value plus a
 * `TotalUnitInterestCard` trigger that opens the per-subtract breakdown popup.
 * The card is suppressed when the slot has no subtracts (nothing to break down).
 * A GIS breakdown's line calculations explain the GIS values (`interestFormula`).
 *
 * @param {SlotInterestRow} slot - The slot row.
 * @param {'am' | 'gis'} source - Which column pair the cell is in.
 * @param {'NRI' | 'WI'} calculationType - Which value the cell shows.
 * @return {JSX.Element} The cell content.
 */
const renderSlotInterestCell = (
  slot: SlotInterestRow,
  source: 'am' | 'gis',
  calculationType: 'NRI' | 'WI'
): JSX.Element => {
  const pair =
    source === 'am'
      ? { NRI: slot.amNri, WI: slot.amWi }
      : { NRI: slot.gisNri, WI: slot.gisWi };
  const value = pair[calculationType];
  return (
    <span className={styles.amCell}>
      <span>{format8(value, '—')}</span>
      {slot.subtracts.length > 0 && (
        <TotalUnitInterestCard
          slotName={slot.wellName}
          items={buildSlotInterestItems(slot, source, calculationType)}
          calculatedValue={value ?? 0}
          calculationType={calculationType}
          title={
            source === 'gis' ? `Total Slot GIS ${calculationType}` : undefined
          }
          lineFormula={interestFormula(isAmSourceSelectable(slot), source)}
        />
      )}
    </span>
  );
};

const columns = [
  // Filter-only ghost column — holds the `view` filter value so
  // ColumnCheckboxGroupFilter can call `table.getColumn('view')`. Hidden from
  // the table render via `initialColumnVisibility` below.
  columnHelper.display({
    id: 'view',
    enableColumnFilter: true,
    enableSorting: false,
    enableHiding: true,
    meta: {
      type: 'enum',
      toggleColumnFilter: false,
      enablePinning: false,
      enableReordering: false,
    },
  }),

  columnHelper.display({
    id: 'status',
    header: 'Status',
    // Wide enough to hold the status label + the "On Hold" tag inline without
    // the tag overflowing into the Subtracts column (INS-9897).
    size: 230,
    // Wrap (not truncate) so the red "On Hold" tag drops to a second line and
    // the row grows to fit, instead of being clipped at the cell edge when the
    // label + tag exceed the column width. The table already renders measured
    // row heights (expandable sub-rows), so this only lets held rows grow.
    meta: { wrap: true },
    enableSorting: true,
    cell: (info) => {
      // Promoted-grain status: Not Started / In Progress / Certified — what
      // the entity master says, unchanged by session confirmations (those
      // surface via the changed-row highlight).
      const status = slotStatus(info.row.original);
      const { Icon } = STATUS_DISPLAY[status];
      const iconClass = {
        not_started: styles.workflowStatusIconUnconfirmed,
        in_progress: styles.workflowStatusIconPartial,
        certified: styles.workflowStatusIconConfirmed,
      }[status];
      return (
        <span className={styles.workflowStatus}>
          <Icon size={16} className={iconClass} />
          {SLOT_STATUS_LABELS[status]}
          {/* Slot-level On Hold rollup (INS-9896): ≥1 held tract. The BE also
              forces status to in_progress, so this rides alongside it. */}
          {info.row.original.onHold && (
            <Tag type="red" size="sm">
              Needs Further Review
            </Tag>
          )}
        </span>
      );
    },
  }),

  // LIVE session certification progress ("2/2 subtracts") — moves as the
  // user certifies, unlike the promoted-grain Status column. Falls back to
  // the promoted progress when the live field is absent (single-slot
  // confirm response).
  columnHelper.display({
    id: 'tracts',
    header: 'Subtracts/UATs',
    // Wide enough for the full "X/Y subtracts X/Y UATs" string (incl. 2-digit
    // counts) so it isn't truncated to "… U…".
    size: 230,
    enableSorting: false,
    cell: (info) => subtractsFraction(info.row.original) ?? '—',
  }),

  columnHelper.accessor('slotId', {
    id: 'slotId',
    header: 'Slot ID',
    size: 140,
    meta: { type: 'text' },
    cell: (info) => {
      const value = info.getValue();
      // INS-11586: `pinned` is set by the BE only on a row the pin INJECTED —
      // one the active view filter had dropped (its last review was just
      // cleared while the Certify panel is open for it). Without the marker the
      // row reads as a filter bug: it sits at the top of the table, outside the
      // sort, under a filter it no longer matches, and isn't in the tile
      // counts. It disappears when the panel closes.
      const pinned = info.row.original.pinned === true;
      return (
        <>
          <Link to={`/wells/${value}`} openInNewTab>
            {value}
          </Link>
          {pinned && (
            // `description`, not `label`: the Tag's own "Pinned" text is the
            // state and has to stay the accessible name — `label` would clobber
            // it. This wires the explanation up as aria-describedby instead, so
            // it's reachable by keyboard and screen reader rather than
            // hover-only (a native `title` is neither). `autoAlign` because the
            // table is a scroll container and a bottom-aligned popover clips.
            <Tooltip
              description="Kept visible while you have this slot open — it no longer matches the current filter."
              align="bottom"
              autoAlign
            >
              <span tabIndex={0} className={styles.pinnedTagTrigger}>
                <Tag type="teal" size="sm">
                  Pinned
                </Tag>
              </span>
            </Tooltip>
          )}
        </>
      );
    },
  }),

  columnHelper.accessor('wellName', {
    id: 'wellName',
    header: 'Well Name',
    size: 180,
    meta: { type: 'text' },
  }),

  // Operator is a denormalized name string (Well#current_operator), not an
  // Entity — there's no operator detail page to link to, so render plain text.
  columnHelper.accessor('operator', {
    id: 'operator',
    header: 'Operator',
    size: 180,
    meta: { type: 'enum' },
  }),

  columnHelper.accessor('unitName', {
    id: 'unitName',
    header: 'Unit',
    size: 140,
    meta: { type: 'text' },
    cell: (info) => {
      const value = info.getValue();
      return (
        <Link
          to={`/development_areas/${encodeURIComponent(value)}`}
          openInNewTab
        >
          {value}
        </Link>
      );
    },
  }),

  // Which unit agreement the row is about. A well can sit in a D&S unit and any
  // number of JOA units; each gets its own row, and this is what tells them
  // apart. Label mapping is shared with the unit page's Slots Details table so
  // the two cannot drift.
  columnHelper.accessor('unitAgreementType', {
    id: 'unitAgreementType',
    header: 'Unit Agreement Type',
    size: 170,
    meta: { type: 'text' },
    cell: (info) => formatUnitAgreementType(info.getValue()),
  }),

  // Filter-only ghost column for AM presence — drives ColumnCheckboxGroupFilter
  // via `table.getColumn('amPresence')`.
  columnHelper.display({
    id: 'amPresence',
    enableColumnFilter: true,
    enableSorting: false,
    enableHiding: true,
    meta: {
      type: 'enum',
      toggleColumnFilter: false,
      enablePinning: false,
      enableReordering: false,
    },
  }),

  columnHelper.accessor('holeDirection', {
    id: 'holeDirection',
    header: 'Hole Direction',
    size: 150,
    // 'enum' (not 'text') — the sidebar ColumnCheckboxGroupFilter enforces a
    // runtime check that the column meta.type is 'enum' or 'boolean'; the
    // cell still renders the raw string. Same contract note as `streams`.
    meta: { type: 'enum' },
    cell: (info) => info.getValue() ?? '—',
  }),

  // Normalized well status (pdp / duc / permit / …). Mirrors the operator
  // column: 'enum' meta (the sidebar filter enforces enum/boolean), no BE sort
  // attribute, raw-value fallback for any unmapped status.
  columnHelper.accessor('wellStatus', {
    id: 'wellStatus',
    header: 'Well Status',
    size: 150,
    enableSorting: false,
    meta: { type: 'enum' },
    cell: (info) => {
      const value = info.getValue();
      return value ? (WELL_STATUS_LABELS[value] ?? value) : '—';
    },
  }),

  // Accessor returns the pre-formatted label so the wrapper sees a string (not
  // an array) and skips the multi-line stacked layout. Otherwise confirmed rows
  // (`numberOfLines: 2`) leave this cell top-aligned while Interest / Evidence
  // Source stay vertically centered.
  columnHelper.accessor((row) => streamsLabel(row.streams), {
    id: 'streams',
    header: 'Stream',
    size: 140,
    // Stays 'enum' — the sidebar `ColumnCheckboxGroupFilter` enforces a runtime
    // check that the column meta.type is 'enum' or 'boolean'. Even though the
    // accessor now returns a string (to skip the wrapper's stacked layout),
    // the filter contract is what drives the meta.type, not the cell value.
    meta: { type: 'enum' },
  }),

  columnHelper.display({
    id: 'reconciledTo',
    header: 'Interest Source',
    size: 180,
    meta: { type: 'enum' },
    cell: (info) => interestSourceLabel(info.row.original.confirmation),
  }),

  columnHelper.display({
    id: 'evidenceSource',
    header: 'Evidence Source',
    size: 180,
    meta: { type: 'enum' },
    cell: (info) => evidenceSourceLabel(info.row.original.confirmation),
  }),

  columnHelper.display({
    id: 'electedInterest',
    header: 'Certified Interest',
    size: 200,
    cell: (info) => {
      const { confirmation } = info.row.original;
      if (confirmation === null) return '—';
      return (
        <div className={styles.electedStack}>
          <span>NRI: {format8(confirmation.confirmedNri)}</span>
          <span>WI: {format8(confirmation.confirmedWi)}</span>
        </div>
      );
    },
  }),

  columnHelper.accessor('amNri', {
    id: 'amNri',
    header: 'AM NRI',
    size: 200,
    meta: {
      type: 'decimal',
      format: { minDecimalPlaces: 8, maxDecimalPlaces: 8 },
    },
    cell: (info) => renderSlotInterestCell(info.row.original, 'am', 'NRI'),
  }),

  // GIS estimate for every slot — beside the AM value when the unit has an
  // Allocation Manager, in place of it when not. Never certifiable to AM.
  columnHelper.accessor('gisNri', {
    id: 'gisNri',
    header: 'GIS NRI',
    size: 200,
    enableSorting: false,
    meta: {
      type: 'decimal',
      format: { minDecimalPlaces: 8, maxDecimalPlaces: 8 },
    },
    cell: (info) => renderSlotInterestCell(info.row.original, 'gis', 'NRI'),
  }),

  columnHelper.accessor('amWi', {
    id: 'amWi',
    header: 'AM WI',
    size: 200,
    meta: {
      type: 'decimal',
      format: { minDecimalPlaces: 8, maxDecimalPlaces: 8 },
    },
    cell: (info) => renderSlotInterestCell(info.row.original, 'am', 'WI'),
  }),

  columnHelper.accessor('gisWi', {
    id: 'gisWi',
    header: 'GIS WI',
    size: 200,
    enableSorting: false,
    meta: {
      type: 'decimal',
      format: { minDecimalPlaces: 8, maxDecimalPlaces: 8 },
    },
    cell: (info) => renderSlotInterestCell(info.row.original, 'gis', 'WI'),
  }),

  // DO NRI / DO WI and the NRI / WI Difference columns are parked until a
  // live Division Order source exists (INS-9476) — there's nothing to show
  // or diff against today. Restore them alongside the live-DO work.

  columnHelper.accessor((row) => row.confirmation?.confirmedAt ?? null, {
    id: 'confirmedAt',
    header: 'Confirmed At',
    size: 160,
    meta: { type: 'date' },
  }),

  // BE has no `confirmed_by` sort attribute (only `confirmed_at`), so this
  // column is intentionally non-sortable until the BE adds one.
  columnHelper.accessor((row) => row.confirmation?.confirmedByName ?? null, {
    id: 'confirmedBy',
    header: 'Confirmed By',
    size: 160,
    enableSorting: false,
    meta: { type: 'text' },
  }),
];

/**
 * Renders the per-row subtract list (INS-9641) inside the DataTable's native
 * row-expansion slot. Wired via `renderRowSubComponent` so the wrapper auto-
 * injects an expander column at position 0 and manages expand/collapse state.
 *
 * @param {Row<SlotRow>} row - The TanStack row from the DataTable wrapper.
 * @return {JSX.Element} The expanded subtable, or an empty-state line.
 */
const renderSubtractsSubRow = (row: Row<SlotRow>): JSX.Element => {
  const slot = row.original;
  if (slot.subtracts.length === 0) {
    return <p className={styles.subtractsEmpty}>No subtracts for this slot.</p>;
  }
  return <SubtractsTable slot={slot} />;
};

/**
 * Orchestrator for the slot comparison table. Reads the workspace identity from
 * `useWorkspaceConfirmation`, drives the per-workspace slot-list search atom,
 * sources operator filter options from the filter-values atom, and wires the
 * per-row Confirm action + bulk toolbar into the Confirm Interest panel.
 *
 * @return {JSX.Element} The rendered table assembly.
 */
export const ComparisonTable = (): JSX.Element => {
  const { workspaceId, workspace } = useWorkspaceConfirmation();
  const searchAtom = slotListAtomFamily({
    surface: 'comparison',
    workspaceId,
  });
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const filterValues = useAtomValue(
    workspaceFilterValuesAtomFamily(workspaceId)
  );
  const setConfirmPanelState = useSetAtom(openConfirmPanelAtom);
  const setUnconfirmTargetRow = useSetAtom(openUnconfirmModalAtom);
  const setSlotDetailTarget = useSetAtom(openSlotDetailAtom);

  // Selection is hoisted to an atom so the Confirm Interest panel's close
  // handlers can clear it directly. The table is its only reader.
  const [selection, setSelection] = useAtom(comparisonTableSelectionAtom);

  // INS-11586: while the Certify panel is open for a single slot, pin that slot
  // into the query. The BE keeps it in the page even after its last review is
  // cleared (which would otherwise drop it from the on_hold/view filter), so the
  // panel's live-row lookup stays on the slot's true post-clear state instead of
  // reverting to the stale open-time snapshot.
  //
  // Declared above the initial-fetch effect because that effect reads it: the
  // effect body only runs post-render so the old ordering was runtime-safe, but
  // it read as a use-before-declaration.
  const panelState = useAtomValue(confirmPanelStateAtom);
  const pinnedSlotId =
    panelState?.mode === 'single' ? panelState.rows[0]?.slotId : undefined;

  // Seeded with the mount-time value so the pin effect below no-ops on the first
  // pass — the initial fetch already carries it. Seeded with `undefined` instead,
  // mounting with the panel ALREADY open fires a second, identical search.
  const lastPinnedSlotId = useRef<string | undefined>(pinnedSlotId);

  // Initial fetch — fires once per workspace. Subsequent fetches are driven by
  // the per-event handlers below (pagination / sort / filter / global search).
  useEffect(() => {
    setSearchPayload(() => ({ ...DEFAULT_PARAMS, workspaceId, pinnedSlotId }));
    // Mount-only: subsequent fetches carry pinnedSlotId via handleStateChange /
    // the pin effect below. eslint-disable so the mount contract stays 1 fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, setSearchPayload]);

  // Opening/closing the panel isn't a table event, so re-fire the search on
  // change (preserving current filters/sort/page). The ref guard skips the
  // mount no-op — no double fetch.
  useEffect(() => {
    if (lastPinnedSlotId.current === pinnedSlotId) return;
    lastPinnedSlotId.current = pinnedSlotId;
    setSearchPayload((prev) => (prev ? { ...prev, pinnedSlotId } : prev));
  }, [pinnedSlotId, setSearchPayload]);

  const { exportPresets, exportStatus, resetExport } = useSlotsExport();

  // Per-event API: a single handler observes the full post-change TableState
  // snapshot and rebuilds the search payload. Wired into every per-event
  // callback the table fires (pagination / sort / filter / global search).
  const handleStateChange = useCallback(
    (state: TableState) => {
      const sort = state.sorting[0];
      setSearchPayload(() => ({
        ...DEFAULT_PARAMS,
        workspaceId,
        page: state.page,
        pageSize: state.pageSize,
        sortAttribute: resolveSortAttribute(sort?.id),
        sortDirection: sort?.desc ? 'DESC' : 'ASC',
        text: state.globalFilter || undefined,
        ...columnFiltersToParams(state.columnFilters),
        // Keep the open slot pinned across filter/sort/page changes (INS-11586).
        pinnedSlotId,
      }));
    },
    [workspaceId, setSearchPayload, pinnedSlotId]
  );

  const handleSelectionChange = useCallback(
    (state: TableState) => setSelection(state.selection),
    [setSelection]
  );

  const totalCount = searchResult.data?.totalCount ?? 0;

  // Confirmed rows render as two-line rows. The DataTable wrapper reads
  // `numberOfLines` off the row and grows its height accordingly. The stamp is
  // render-only — `selectionRows` below deliberately reads the raw
  // `searchResult.data?.results` (not `tableData`) so the inflated row never
  // leaks into a confirm payload.
  const tableData = useMemo<SlotRow[]>(
    () =>
      (searchResult.data?.results ?? []).map((row) => {
        if (row.confirmation !== null) {
          return { ...row, numberOfLines: 2 };
        }
        return row as SlotRow;
      }),
    [searchResult.data?.results]
  );

  // Rows changed in THIS workspace session get the standard blue row highlight.
  // "Changed this session" is any unpromoted activity — a confirm/unconfirm or a
  // hold placed/released — which the BE computes per row (`unpromotedActivity`,
  // INS-9896). It excludes carried-forward seeds (confirmations OR holds), so a
  // carried hold no longer falsely highlights a do-nothing workspace. (This is
  // deliberately distinct from `onHold`, which is the current hold STATE.)
  const changedRowIds = useMemo(
    () =>
      new Set(
        (searchResult.data?.results ?? [])
          .filter((row) => row.unpromotedActivity)
          .map((row) => rowKey(row))
      ),
    [searchResult.data?.results]
  );

  // Header select-all is disabled (isBulkSelectEnabled: false), so `selectAll`
  // is always false — the selection is exactly the rows the user ticked.
  const selectionRows = useMemo(
    () =>
      (searchResult.data?.results ?? []).filter((row) =>
        selection.toggledNodes.includes(rowKey(row))
      ),
    [searchResult.data?.results, selection.toggledNodes]
  );

  const operators =
    filterValues.state === 'hasData' ? filterValues.data.operators : [];
  const wellStatuses =
    filterValues.state === 'hasData' ? filterValues.data.wellStatuses : [];

  const confirmDisabled = workspace.promoted || selectionRows.length === 0;
  const confirmLabel = confirmDisabled
    ? 'Certify Interest'
    : `Certify Interest (${format0(selectionRows.length)})`;

  // The per-row Confirm + Unconfirm actions are built in-component (not in the
  // module-scope `columns`) so each cell can reach the panel-state / modal
  // setters and the promoted gate.
  const actionsColumn = useMemo(
    () =>
      columnHelper.display({
        id: '_actions',
        header: 'Actions',
        size: 152,
        enableSorting: false,
        meta: {
          toggleColumnFilter: false,
          enablePinning: false,
          enableReordering: false,
        },
        cell: (info) => {
          const { slotId } = info.row.original;
          return (
            <div className={styles.actionsCell}>
              <Button
                hasIconOnly
                kind="ghost"
                size="sm"
                renderIcon={CheckmarkFilled}
                iconDescription={`Certify interest for slot ${slotId}`}
                disabled={workspace.promoted}
                onClick={() =>
                  setConfirmPanelState({
                    mode: 'single',
                    rows: [info.row.original],
                  })
                }
              />
              <IconButton
                kind="ghost"
                size="sm"
                label="Show details"
                align="left"
                autoAlign
                onClick={() =>
                  setSlotDetailTarget({
                    workspaceSlotId: info.row.original.workspaceSlotId,
                    slotId: info.row.original.slotId,
                    wellName: info.row.original.wellName,
                  })
                }
              >
                <Information />
              </IconButton>
              <Button
                hasIconOnly
                kind="ghost"
                size="sm"
                renderIcon={Undo}
                iconDescription={`Remove certification for slot ${slotId}`}
                disabled={
                  workspace.promoted || info.row.original.confirmation === null
                }
                onClick={() => setUnconfirmTargetRow(info.row.original)}
              />
            </div>
          );
        },
      }),
    [
      setConfirmPanelState,
      setUnconfirmTargetRow,
      setSlotDetailTarget,
      workspace.promoted,
    ]
  );

  const tableColumns = useMemo(
    () => [...columns, actionsColumn],
    [actionsColumn]
  );

  // Controlled selection: the atom is the source of truth — the table reflects it via
  // `controlledState.selection` and reports clicks via `onSelectionChange` → the atom, so
  // the Confirm Interest panel's close handlers can clear the table by writing the atom.
  const tableInitialState = useMemo<InitialTableState>(
    () => ({
      sortColumn: 'slotId',
      sortDirection: 'asc',
      pageSize: 100,
      columnVisibility: { view: false, amPresence: false },
    }),
    []
  );

  return (
    <div className={styles.tableWrapper}>
      <SummaryTiles aggregations={searchResult.data?.aggregations} />
      <ServerExportStatusBanner
        status={exportStatus}
        onDismiss={resetExport}
        successTitle="Slots exported"
        failureTitle="Couldn't export slots."
        processingTitle="Exporting slots…"
      />
      {/* tableArea constrains the height:100% DataTable to the space left
          below the tiles/banner — see comparison-table.module.scss. */}
      <div className={styles.tableArea}>
        <DataTable<SlotRow>
          data={tableData}
          columns={tableColumns as DataTableColumnDef<SlotRow>[]}
          getRowId={rowKey}
          highlightedRowIds={changedRowIds}
          disableSelection={workspace.promoted}
          initialTableState={tableInitialState}
          controlledState={{ selection }}
          persistenceKey="interest-certification-comparison-table"
          isLoading={searchResult.loading ?? false}
          error={searchResult.error ?? null}
          renderRowSubComponent={renderSubtractsSubRow}
          scroll={{
            mode: 'paginated',
            serverDriven: true,
            totalCount,
          }}
          config={{
            // Filtering is driven by the left sidebar (renderFilterPanel), not
            // the inline column-filter row — keep it off.
            columnFilters: false,
            export: { exportPresets },
            selectionType: 'multi',
            isBulkSelectEnabled: false,
          }}
          onPaginationChange={handleStateChange}
          onSortChange={handleStateChange}
          onFilterChange={handleStateChange}
          onGlobalSearchChange={handleStateChange}
          onSelectionChange={handleSelectionChange}
          renderFilterPanel={() => (
            <SlotFilters operators={operators} wellStatuses={wellStatuses} />
          )}
          searchPlaceholder="Search by Slot ID, well name, operator, or well status"
          rightToolbarActions={[
            <Button
              key="confirm-interest"
              kind="primary"
              size="lg"
              disabled={confirmDisabled}
              onClick={() =>
                setConfirmPanelState(
                  selectionRows.length === 1
                    ? { mode: 'single', rows: [selectionRows[0]] }
                    : { mode: 'bulk', rows: selectionRows }
                )
              }
            >
              {confirmLabel}
            </Button>,
          ]}
        />
      </div>
    </div>
  );
};
